// apps/client/src/components/reports/VarianceDetailModal.tsx
// Modal shown when a user clicks a loan in the variance drill-down. Shows the variance
// detail breakdown plus a form to mark the loan as construction / data-error / excluded.

import React, { useState } from 'react';
import { X, Save, AlertTriangle } from 'lucide-react';
import { formatCurrencyShorthand } from '../../utils/formatters';
import {
  useUpsertLoanAnnotation,
  type LoanAnnotationClassification,
} from '../../hooks/reports/useUpsertLoanAnnotation';
import type { VarianceLoanRow } from '../../hooks/reports/useApprovedVsSettledVariance';
import { useAuth } from '../../hooks/useAuth';
import { BROKER_FILTERED_ADMIN_COPY } from '../../utils/brokerFilteredAdmin';

interface VarianceDetailModalProps {
  loan: VarianceLoanRow;
  companyId: string;
  onClose: () => void;
  onSaved?: () => void;
}

const CLASSIFICATIONS: { value: LoanAnnotationClassification; label: string; description: string }[] = [
  { value: null,             label: 'No classification', description: 'Treat as a normal shortfall.' },
  { value: 'construction',   label: 'Construction draw', description: 'Multi-tranche loan; further upfronts pending.' },
  { value: 'split-tranche',  label: 'Split tranche',     description: 'Loan settled in stages; not lost commission.' },
  { value: 'data-error',     label: 'Data error',        description: 'Approved/settled values are wrong in source.' },
  { value: 'other',          label: 'Other',             description: 'Reason captured in notes below.' },
];

export const VarianceDetailModal: React.FC<VarianceDetailModalProps> = ({
  loan,
  companyId,
  onClose,
  onSaved,
}) => {
  // `upsertLoanAnnotation` does NOT opt in to allowBrokerFilteredAdmin, so it
  // is default-denied to a broker-restricted admin at the chokepoint.
  const { isBrokerFilteredAdmin } = useAuth();
  const { upsert, loading, error } = useUpsertLoanAnnotation();

  const [classifiedAs, setClassifiedAs] = useState<LoanAnnotationClassification>(
    null /* annotation classification not surfaced in row; user (re)selects */,
  );
  const [excludedFromVariance, setExcludedFromVariance] = useState(loan.isUserExcluded);
  const [reason, setReason] = useState(loan.userAnnotationReason ?? '');

  const handleSave = async () => {
    await upsert({
      companyId,
      loanId: loan.loanId,
      excludedFromVariance,
      classifiedAs,
      reason,
    });
    onSaved?.();
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-gray-900 border border-gray-700 rounded-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto shadow-2xl">
        {/* Header */}
        <div className="flex items-start justify-between px-6 py-4 border-b border-gray-700/50">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-lg font-bold text-white">{loan.customerName || `Loan ${loan.loanId}`}</h2>
              <span className="text-xs font-mono px-2 py-0.5 rounded bg-gray-700/60 text-gray-400">
                {loan.formulaPath === '3-column' ? '3-col' : '2-col'}
              </span>
              {loan.isConstructionLikely && (
                <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded bg-amber-500/15 text-amber-400">
                  <AlertTriangle className="w-3 h-3" />
                  Construction (likely)
                </span>
              )}
            </div>
            <p className="text-sm text-gray-400 mt-1">
              {loan.lender || '—'}{' · '}{loan.brokerDisplay || '—'}{' · settled '}{loan.settlementDate || '—'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition flex-shrink-0 ml-4"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Variance numbers */}
        <div className="px-6 py-4 border-b border-gray-700/50">
          <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Variance breakdown</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
            <div>
              <div className="text-gray-500 text-xs uppercase tracking-wider">Approved</div>
              <div className="text-white font-semibold tabular-nums mt-1">{formatCurrencyShorthand(loan.approvedCents)}</div>
            </div>
            <div>
              <div className="text-gray-500 text-xs uppercase tracking-wider">Settled</div>
              <div className="text-white font-semibold tabular-nums mt-1">{formatCurrencyShorthand(loan.settledCents)}</div>
            </div>
            {loan.subsequentAmountCents > 0 && (
              <div>
                <div className="text-gray-500 text-xs uppercase tracking-wider">Top-up</div>
                <div className="text-white font-semibold tabular-nums mt-1">{formatCurrencyShorthand(loan.subsequentAmountCents)}</div>
                <div className="text-gray-500 text-xs mt-0.5">subsequent upfront {formatCurrencyShorthand(loan.subsequentUpfrontCents)}</div>
              </div>
            )}
            <div>
              <div className="text-gray-500 text-xs uppercase tracking-wider">Shortfall %</div>
              <div className="text-white font-semibold tabular-nums mt-1">{(loan.shortfallPct * 100).toFixed(1)}%</div>
            </div>
            <div>
              <div className="text-gray-500 text-xs uppercase tracking-wider">Upfront paid</div>
              <div className="text-white font-semibold tabular-nums mt-1">{formatCurrencyShorthand(loan.upfrontActualCents)}</div>
            </div>
            <div>
              <div className="text-gray-500 text-xs uppercase tracking-wider">Implied at full</div>
              <div className="text-white font-semibold tabular-nums mt-1">{formatCurrencyShorthand(loan.upfrontImpliedFullCents)}</div>
            </div>
            <div>
              <div className="text-gray-500 text-xs uppercase tracking-wider">Decrease</div>
              <div className="font-semibold tabular-nums mt-1" style={{ color: loan.upfrontDecreaseCents > 0 ? '#f87171' : '#9ca3af' }}>
                {formatCurrencyShorthand(loan.upfrontDecreaseCents)}
              </div>
            </div>
          </div>
          <div className="mt-3 text-xs text-gray-500 grid grid-cols-2 gap-2">
            <div>Loan ID: <span className="font-mono text-gray-400">{loan.loanId}</span></div>
            <div>Entity: <span className="text-gray-400">{loan.canonicalEntity || '—'}</span></div>
          </div>
        </div>

        {/* Annotation form */}
        <div className="px-6 py-4">
          <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Classification (admin override)</h3>

          <div className="space-y-2">
            {CLASSIFICATIONS.map(c => (
              <label
                key={c.value ?? '__none__'}
                className={`flex items-start gap-3 p-2.5 rounded-lg cursor-pointer transition ${
                  classifiedAs === c.value
                    ? 'bg-blue-500/10 border border-blue-500/40'
                    : 'bg-gray-800/40 border border-transparent hover:bg-gray-800/70'
                }`}
              >
                <input
                  type="radio"
                  name="classification"
                  checked={classifiedAs === c.value}
                  onChange={() => setClassifiedAs(c.value)}
                  className="mt-0.5"
                />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-white">{c.label}</div>
                  <div className="text-xs text-gray-400">{c.description}</div>
                </div>
              </label>
            ))}
          </div>

          <label className="flex items-start gap-3 mt-4 p-2.5 rounded-lg bg-gray-800/40 cursor-pointer hover:bg-gray-800/70 transition">
            <input
              type="checkbox"
              checked={excludedFromVariance}
              onChange={(e) => setExcludedFromVariance(e.target.checked)}
              className="mt-0.5"
            />
            <div>
              <div className="text-sm font-medium text-white">Exclude from variance KPIs</div>
              <div className="text-xs text-gray-400">Loan still appears in the drill-down (greyed) but its decrease is removed from the headline numbers.</div>
            </div>
          </label>

          <div className="mt-4">
            <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
              Notes (optional, max 500 chars)
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value.slice(0, 500))}
              rows={3}
              placeholder="Why this classification? Future you will thank you."
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-gray-200 placeholder-gray-500 focus:outline-none focus:border-blue-500 transition"
            />
            <div className="text-xs text-gray-500 mt-1">{reason.length}/500</div>
          </div>

          {error && (
            <div className="mt-3 text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded px-3 py-2">
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-700/50 flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-300 hover:text-white transition"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isBrokerFilteredAdmin || loading}
            title={isBrokerFilteredAdmin ? BROKER_FILTERED_ADMIN_COPY : undefined}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition"
          >
            <Save className="w-4 h-4" />
            {loading ? 'Saving…' : 'Save annotation'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default VarianceDetailModal;
